/**
 * Detects when a newer build of skyHire has been deployed while a tab is still
 * running the bundle it first loaded.
 *
 * A single-page app never re-fetches index.html as the user navigates, so a tab
 * left open across a deploy keeps executing its original JS. It then runs stale
 * code against a moved API until someone happens to reload, and the failure is
 * silent: the page looks fine and simply behaves like an older version of
 * itself. Worse, a lazily loaded route whose chunk no longer exists on the
 * server fails outright, because the hashed filename it was built to import is
 * gone.
 *
 * watchForNewDeployOnFocus compares the hashed entry bundle this tab is RUNNING
 * against the one currently DEPLOYED, read from a fresh uncached index.html,
 * and fires a callback when they differ so the UI can offer a refresh. It
 * checks only when the tab regains focus, and never reloads on its own: the
 * user picks the moment, so half-written work is not thrown away.
 *
 * Ported from the same utility in skyAdvisor and skySchedule, deliberately
 * unchanged in behaviour so all three apps detect a deploy the same way.
 * No-ops in dev, where Vite serves /src/index.tsx rather than /assets/..., and
 * on any fetch failure, where it treats the build as unchanged and tries again
 * on the next focus.
 */

/** The hashed entry bundle this tab is RUNNING (null in dev). */
function runningEntryPath(): string | null {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'),
  );
  for (const s of scripts) {
    try {
      const p = new URL(s.src, window.location.origin).pathname;
      if (p.startsWith("/assets/")) return p;
    } catch {
      /* malformed src, ignore */
    }
  }
  return null;
}

/**
 * The hashed entry bundle currently DEPLOYED.
 *
 * Reads the SPA shell, which the server sends for any unmatched path with
 * Cache-Control: no-store, so this sees the live index.html rather than the
 * copy this tab booted from. The cache-busting query is belt and braces
 * against an intermediate proxy.
 */
async function deployedEntryPath(): Promise<string | null> {
  try {
    const res = await fetch(`/?_dv=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(/<script[^>]+type="module"[^>]+src="(\/assets\/[^"]+\.js)"/);
    return m ? m[1] : null;
  } catch {
    // Offline or blocked. Treat as no change and retry on the next focus.
    return null;
  }
}

/**
 * Watch, on tab focus only, for a newer deployed build, and call `onNewVersion`
 * once when one is detected. Returns an unsubscribe function. No-ops in dev.
 */
export function watchForNewDeployOnFocus(onNewVersion: () => void): () => void {
  const running = runningEntryPath();
  if (!running) return () => {}; // dev, or an unrecognised shape: nothing to compare
  let notified = false;
  const check = async () => {
    if (notified || document.visibilityState !== "visible") return;
    const deployed = await deployedEntryPath();
    if (deployed && deployed !== running) {
      notified = true;
      onNewVersion();
    }
  };
  document.addEventListener("visibilitychange", check);
  return () => document.removeEventListener("visibilitychange", check);
}
